import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import TableLoader from './Loader/TableLoader';

interface Item {
  id: number;
  ticket_group_code: string;
}

interface CurrentLatestTableProps {
  items: Item[];
  loading: boolean;
  onDelete: (id: number) => void;
  onEdit: (id: number) => void;
}

export default function CurrentLatestSeatPlanTable({
  items,
  loading,
  onDelete,
  onEdit,
}: CurrentLatestTableProps) {
  const [latestPage, setLatestPage] = useState(1);
  const latestPerPage = 10;
  const totalPages = Math.ceil(items.length / latestPerPage);
  const indexOfLast = latestPage * latestPerPage;
  const indexOfFirst = indexOfLast - latestPerPage;
  const currentItems = items.slice(indexOfFirst, indexOfLast);

  const nextPage = () => {
    if (latestPage < totalPages) setLatestPage(latestPage + 1);
  };

  const prevPage = () => {
    if (latestPage > 1) setLatestPage(latestPage - 1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [latestPage]);

  return (
    <>
      {loading ? 
        (
          <TableLoader />
        )

        :

        (
          <table>
            <thead className="bg-gray-200">
              <tr>
                <th style={{ width: '90%' }}>Ticket Group Code</th>
                <th style={{ width: '10%' }} className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length != 0 ? 
                (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.ticket_group_code}</td>
                      <td className="text-center">
                        <button onClick={() => onEdit(item.id)} data-tooltip-id="edit">
                          <FontAwesomeIcon icon={faPenToSquare} className="text-sx text-white bg-red-600 rounded-md p-1 mr-2" />
                        </button>
                        <button onClick={() => onDelete(item.id)} data-tooltip-id="delete">
                          <FontAwesomeIcon icon={faTrash} className="text-sm text-white bg-gray-600 rounded-md p-1" />
                        </button>
                      </td>
                    </tr>
                  ))
                )

                :

                (
                  <tr>
                    <td colSpan={2} className="text-center">No data available!</td>
                  </tr>
                ) 
              }
            </tbody>
          </table>
        )
      }

      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-4 space-x-2">
          <button
            onClick={() => setLatestPage(1)}
            disabled={latestPage === 1}
            className="px-3 py-1 rounded bg-gray-500 text-white disabled:opacity-50">
            First
          </button>

          <button
            onClick={prevPage}
            disabled={latestPage === 1}
            className="px-3 py-1 rounded bg-gray-500 text-white disabled:opacity-50">
            Prev
          </button>

          {[...Array(totalPages)].map((_, index) => {
            const pageNum = index + 1;
            const range = 1;
            const inRange = pageNum >= latestPage - range && pageNum <= latestPage + range;

            if (inRange) {
              return (
                <button
                  key={pageNum}
                  onClick={() => setLatestPage(pageNum)}
                  className={`px-3 py-1 rounded ${
                    latestPage === pageNum
                      ? "bg-red-600 text-white"
                      : "bg-gray-200 text-black"
                  }`}
                >
                  {pageNum}
                </button>
              );
            }
            return null;
          })}

          <button
            onClick={nextPage}
            disabled={latestPage === totalPages}
            className="px-3 py-1 rounded bg-red-500 text-white disabled:opacity-50">
            Next
          </button>

          <button
            onClick={() => setLatestPage(totalPages)}
            disabled={latestPage === totalPages}
            className="px-3 py-1 rounded bg-red-500 text-white disabled:opacity-50">
            Last
          </button>
        </div>
      )}
    </>
  );
}
